"use client";
import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  CategoryTabIcon,
  ColorTabIcon,
  FabricTabIcon,
  FilterIcon,
  PriceTabIcon,
  RatingsTabIcon,
  SizeTabIcon,
  StarFillIcon,
  StarUnFillIcon,
  SubcategoryTabIcon,
} from "@/components/icons/categoriespage-icon";

import { useDispatch, useSelector } from "react-redux";
import {
  clearProduct,
  fetchProductById,
  fetchProducts,
  fetchSearchProducts,
} from "@/redux/slices/productSlice";
import {
  fetchAllGroups,
  fetchCategoryDetails,
} from "@/redux/slices/categorySlice";
import { LoaderContext } from "@/contexts/LoaderContext";
import { useParams, useSearchParams } from "next/navigation";
import Image from "next/image";
import dynamic from "next/dynamic";
import { Button, Dropdown } from "react-bootstrap";
import Link from "next/link";
import { CloseIcon } from "@/components/icons/common-button-icon";
const MainPoster = dynamic(() => import("@/components/mainposter"));
const ProductCardMain = dynamic(() =>
  import("@/components/product-card/productcard-main")
);
const SearchBox = dynamic(() => import("@/components/searchbox"));
const FilterPopup = dynamic(() => import("@/components/filter-popup"));
const PermissionPopup = dynamic(() => import("@/components/permission-popup"));
// const Pagination = dynamic(() => import("@/components/pagination"));
const QuickLookPopup = dynamic(() => import("@/components/quick-look-popup"), {
  ssr: false,
});
const ScrollLoader = dynamic(() => import("@/components/scroll-loader"));
const CommonBtn = dynamic(() => import("@/components/buttons/CommonBtn"));
// const ComanButton = dynamic(() => import("@/components/coman-button"));
const CategoryPages = () => {
  // Filter UseState

  // Loader
  const { showLoader, hideLoader, loading } = useContext(LoaderContext);
  // Router params / query
  const searchParams = useSearchParams();
  const searchBar = searchParams.get("query"); // Get query parameter from URL
  const params = useParams();
  const slug = params.slug;

  // Redux
  const dispatch = useDispatch();
  // const basePrice =  products.map((item)=>item.base_price);
  const { categoryDetails } = useSelector((state) => state.categories);
  const attributes = useSelector((state) => state.attributes.attributes);
  const categories = useSelector((state) => state.categories.categories);
  const products = useSelector((state) => state.products.products);
  const latestProducts = useSelector((state) => state.products.latestProducts);
  const popularProducts = useSelector(
    (state) => state.products.popularProducts
  );
  const bestSellerProducts = useSelector(
    (state) => state.products.bestSellerProducts
  );
  const productsByGroup = useSelector((state) => state.products.productsGroup);
  const allGroups = useSelector((state) => state.categories.allGroups);
  const groupDetails = useSelector(
    (state) => state.categories?.groupDetails?.data
  );
  const { product } = useSelector((state) => state.products);

  // states
  const [show, setShow] = useState(false);
  const [show404, setShow404] = useState(false);
  const [validated, setValidated] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(24);
  const [localProducts, setLocalProducts] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [scrollLoading, setScrollLoading] = useState(false);
  const [sort, setSort] = useState("created_at"); // Default sorting column
  const [order, setOrder] = useState("desc");
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState({
    subcategory: [],
    attributes: {},
    price_min: null,
    price_max: null,
    rating: null,
  });
  const [selectedSort, setSelectedSort] = useState(); // Default text
  const [sortApplied, setSortApplied] = useState(false);
  const [showQuickLook, setShowQuickLook] = useState(false);

  // refs
  const filtersAppliedRef = useRef(false);

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  // Permission  UseState
  const handleOpen = () => setOpen(true);
  const handleHide = () => setOpen(false);

  // const categorySlug = groupDetails?.categories.map(
  //   (category) => category.slug
  // );
  const categorySlug = useMemo(
    () => groupDetails?.categories?.map((cat) => cat.slug) || [],
    [groupDetails]
  );

  const categoryCount = categories?.data?.find((cat) => cat.slug === slug);

  const tabCount = categoryCount?.children?.length || 0;
  useEffect(() => {
    if (show404) return;
    if (!allGroups?.data?.length) {
      dispatch(fetchAllGroups());
    }
  }, [dispatch]); // Runs only if `allGroups` is empty

  useEffect(() => {
    if (show404 || !searchBar) return;

    const fetchSearchData = async () => {
      showLoader();
      setPage(1); // Reset page for scroll
      setHasMore(true); // Reset scroll limit
      setLocalProducts([]); // Clear existing data

      try {
        const response = await dispatch(
          fetchSearchProducts({ search: searchBar })
        ).unwrap();
        setLocalProducts(response?.products || []);
        setHasMore((response?.products?.length || 0) === limit); // Enable scroll if limit matched
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        hideLoader();
      }
    };

    fetchSearchData();
  }, [dispatch, searchBar, show404]);

  // useEffect(() => {
  //   if (slug) {
  //     dispatch(fetchAttributes(slug));
  //   }
  // }, [dispatch, slug]);

  useEffect(() => {
    const predefinedSlugs = [
      "popular-products",
      "latest-arrival-products",
      "casual-wear-products",
      "tunic-cord-set-products",
      "best-seller-products",
    ];

    const validateCategory = async () => {
      try {
        // showLoader();

        if (predefinedSlugs.includes(slug)) {
          dispatch({ type: "categories/resetCategoryDetails" });
          setValidated(true);
          return;
        }

        const categoryResp = await dispatch(
          fetchCategoryDetails(slug)
        ).unwrap();
        if (!categoryResp?.data) {
          setShow404(true);
        }
      } catch (err) {
        setShow404(true);
      } finally {
        // hideLoader();
        setValidated(true);
      }
    };

    if (!searchBar) {
      validateCategory();
    } else {
      setValidated(true); // skip validation on search page
    }
  }, [dispatch, slug]);

  const latestProductsCategory = latestProducts?.products?.map(
    (item) => item.category?.slug
  );
  const popularProductsCategory = popularProducts?.products?.map(
    (item) => item.category?.slug
  );

  useEffect(() => {
    if (searchBar || show404 || !slug) return;

    const fetchData = async () => {
      try {
        if (page === 1) showLoader(); // ✅ Only show full loader on page 1

        const fetchParams = {
          page,
          limit,
          sort,
          order,
          search: searchQuery,
        };

        if (slug === "popular-products") {
          fetchParams.popular = true;
        } else if (slug === "latest-arrival-products") {
          fetchParams.latest = true;
        } else if (slug === "best-seller-products") {
          fetchParams.bestseller = true;
        } else {
          const group = allGroups?.data?.find((g) => {
            return (
              (slug === "casual-wear-products" &&
                g.group_name === "Casual Wear") ||
              (slug === "tunic-cord-set-products" &&
                g.group_name === "Cord Set & Tunic")
            );
          });

          if (group) {
            fetchParams.group = group.group_id;
          } else {
            fetchParams.current_category = slug;
          }
        }
        setScrollLoading(true); // ⬅️ Start scroll loading
        const response = await dispatch(
          fetchProducts({ params: fetchParams })
        ).unwrap();

        const newProducts = response?.products || [];
        setHasMore(newProducts.length === limit);

        if (page === 1) {
          setLocalProducts(newProducts); // replace
          setTotalRecords(response?.pagination?.total || 0);
        } else {
          setLocalProducts((prev) => [...prev, ...newProducts]); // append
        }
        setScrollLoading(false);
      } catch (err) {
        setScrollLoading(false);
        console.error("Error loading products:", err);
      } finally {
        hideLoader();
      }
    };

    fetchData();
  }, [dispatch, slug, page, limit, allGroups?.data]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const innerHeight = window.innerHeight;
      const scrollHeight = document.body.scrollHeight;

      // Start loading 300px before the bottom
      if (
        scrollY + innerHeight >= scrollHeight - 900 &&
        hasMore &&
        !loading &&
        !scrollLoading &&
        !show404 &&
        slug &&
        localProducts.length > 0
      ) {
        setPage((prevPage) => prevPage + 1);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loading, scrollLoading, slug, searchBar, show404]);

  const [filterPopupData, setFilterPopupData] = useState([]);

  const handleApplyFilters = () => {
    const params = {
      page: 1,
      limit,
      sort,
      order,
      search: searchQuery,
    };

    // Handle slug or group-based logic
    const group = allGroups?.data?.find((g) => {
      return (
        (slug === "casual-wear-products" && g.group_name === "Casual Wear") ||
        (slug === "tunic-cord-set-products" &&
          g.group_name === "Cord Set & Tunic")
      );
    });

    if (group) {
      params.group = group.group_id;
    } else if (slug === "popular-products") {
      params.popular = true;
    } else if (slug === "latest-arrival-products") {
      params.latest = true;
    } else if (slug === "best-seller-products") {
      params.bestseller = true;
    } else if (searchBar) {
      params.search = searchBar;
    } else {
      params.current_category = slug;
    }

    // ✅ Handle category/subcategory/size/color
    const selectedCategories = [
      ...(selectedFilters.category || []),
      ...(selectedFilters.subcategory || []),
    ];

    if (selectedCategories.length > 0) {
      params.category = selectedCategories.join(",");
    }

    if (selectedFilters.color?.length > 0) {
      params.colors = selectedFilters.color.join(",");
    }

    if (selectedFilters.size?.length > 0) {
      params.sizes = selectedFilters.size.join(",");
    }

    if (
      selectedFilters.price_min !== null &&
      selectedFilters.price_max !== null
    ) {
      params.price_min = selectedFilters.price_min;
      params.price_max = selectedFilters.price_max;
    }

    if (selectedFilters.rating !== null) {
      params.rating = selectedFilters.rating;
    }

    // ✅ Handle dynamic attributes like "saree-length", "material"
    // Dynamic attributes
    Object.entries(selectedFilters).forEach(([key, values]) => {
      const knownKeys = [
        "category",
        "subcategory",
        "color",
        "size", // ← color should prevent this
        "colors",
        "sizes",
        "price_min",
        "price_max",
        "rating",
      ];
      if (
        !knownKeys.includes(key) &&
        Array.isArray(values) &&
        values.length > 0
      ) {
        params[`attribute[${key}]`] = values.join(",");
      }
    });

    const filtersSelected = Object.entries(selectedFilters).some(
      ([key, value]) => {
        // Skip primitive values like price/rating — we'll handle them separately
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        if (key === "price_min" || key === "price_max") {
          return (
            selectedFilters.price_min !== null &&
            selectedFilters.price_max !== null
          );
        }
        if (key === "rating") {
          return selectedFilters.rating !== null;
        }
        return false;
      }
    );

    filtersAppliedRef.current = filtersSelected;
    setFiltersApplied(filtersSelected);

    // Fetch API
    showLoader();
    setPage(1);
    setHasMore(true);

    const fetchFn = searchBar ? fetchSearchProducts : fetchProducts;

    dispatch(fetchFn(searchBar ? { ...params } : { params }))
      .unwrap()
      .then((res) => {
        const productsData = searchBar ? res.products : res.products || [];
        setLocalProducts(productsData);
        setHasMore(productsData.length === limit);
      })
      .catch((err) => {
        console.error("Error applying filters:", err);
      })
      .finally(() => {
        hideLoader();
        // setShow(false);
      });
  };

  useEffect(() => {
    if (show404 || !show) return;

    let filtersData = products.filters;

    if (slug === "popular-products" && popularProducts?.filters) {
      filtersData = popularProducts.filters;
    } else if (slug === "latest-arrival-products" && latestProducts?.filters) {
      filtersData = latestProducts.filters;
    } else if (slug === "best-seller-products" && bestSellerProducts?.filters) {
      filtersData = bestSellerProducts.filters;
    } else {
      const group = allGroups?.data?.find((g) => {
        return (
          (slug === "casual-wear-products" && g.group_name === "Casual Wear") ||
          (slug === "tunic-cord-set-products" &&
            g.group_name === "Cord Set & Tunic")
        );
      });

      if (group && productsByGroup?.filters) {
        filtersData = productsByGroup.filters;
      }
    }

    if (!filtersData || Object.keys(filtersData).length === 0) return;

    const dynamicData = [];

    // ✅ Categories & Subcategories

    if (
      [
        "popular-products",
        "latest-arrival-products",
        "casual-wear-products",
        "tunic-cord-set-products",
        "best-seller-products",
      ].includes(slug)
    ) {
      if (filtersData.categories?.length > 0) {
        dynamicData.push({
          icon: <CategoryTabIcon />,
          title: "Category",
          slug: "category",
          number: selectedFilters?.category?.length || "",
          content: filtersData.categories.map((cat) => ({
            name: cat.name,
            slug: cat.slug,
            count: cat.product_count,
          })),
        });

        const subcategories = filtersData.categories.flatMap(
          (cat) => cat.subcategories || []
        );
        if (subcategories.length > 0) {
          dynamicData.push({
            icon: <SubcategoryTabIcon />,
            title: "Subcategory",
            slug: "subcategory",
            number: selectedFilters?.subcategory?.length || "",
            content: subcategories.map((sub) => ({
              name: sub.category_name,
              slug: sub.slug,
              count: sub.product_count > 0 ? sub.product_count : "",
            })),
          });
        }
      }
    } else {
      // Default behavior for other categories
      if (
        filtersData.categories?.length > 0 ||
        filtersData.categories?.values?.length > 0
      ) {
        dynamicData.push({
          icon: <SubcategoryTabIcon />,
          title: "Subcategory",
          slug: "subcategory",
          number: selectedFilters?.subcategory?.length || "",
          content: filtersData.categories[0]?.subcategories?.map((sub) => ({
            name: sub.category_name,
            slug: sub.slug,
            count: sub.product_count > 0 ? sub.product_count : "",
          })),
        });
      }
    }

    // ✅ Colors
    if (filtersData.colors?.length > 0) {
      dynamicData.push({
        icon: <ColorTabIcon />,
        title: "Color",
        slug: "color",
        number: selectedFilters?.color?.length || "",
        content: filtersData.colors.map((color) => ({
          name: color.value_name,
          id: color.value_id,
          count: color.product_count > 0 ? color.product_count : "",
        })),
      });
    }

    // ✅ Sizes
    if (filtersData.sizes?.length > 0) {
      dynamicData.push({
        icon: <SizeTabIcon />,
        title: "Size",
        slug: "size",
        number: selectedFilters?.size?.length || "",
        content: filtersData.sizes.map((size) => ({
          name: size.value_name,
          id: size.value_id,
          count: size.product_count > 0 ? size.product_count : "",
        })),
      });
    }

    // ✅ Price Range
    if (filtersData.price_range?.min && filtersData.price_range?.max) {
      dynamicData.push({
        icon: <PriceTabIcon />,
        title: "Price",
        slug: "price",
        number: selectedFilters?.rating?.length || "",
        content: [
          {
            minimum: parseFloat(filtersData.price_range.min),
            maximum: parseFloat(filtersData.price_range.max),
          },
        ],
      });
    }

    const generateStars = (filled) => {
      return Array.from({ length: 5 }, (_, index) =>
        index < filled ? (
          <StarFillIcon key={index} />
        ) : (
          <StarUnFillIcon key={index} />
        )
      );
    };

    if (filtersData?.ratings && filtersData.ratings.length > 0) {
      const ratingsMap = {};
      filtersData.ratings.forEach((r) => {
        ratingsMap[r.rating] = r.product_count;
      });

      dynamicData.push({
        icon: <RatingsTabIcon />,
        title: "Rating",
        slug: "ratings",
        number: selectedFilters?.rating ? "1" : "",
        content: [
          {
            value: 5,
            count: ratingsMap[5] > 0 ? ratingsMap[5] : "",
            label: <div className="d-flex gap-2">{generateStars(5)}</div>,
          },
          {
            value: 4,
            count: ratingsMap[4] > 0 ? ratingsMap[4] : "",
            label: <div className="d-flex gap-2">{generateStars(4)}</div>,
          },
          {
            value: 3,
            count: ratingsMap[3] > 0 ? ratingsMap[3] : "",
            label: <div className="d-flex gap-2">{generateStars(3)}</div>,
          },
          {
            value: 2,
            count: ratingsMap[2] > 0 ? ratingsMap[2] : "",
            label: <div className="d-flex gap-2">{generateStars(2)}</div>,
          },
          {
            value: 1,
            count: ratingsMap[1] > 0 ? ratingsMap[1] : "",
            label: <div className="d-flex gap-2">{generateStars(1)}</div>,
          },
        ],
      });
    }

    // ✅ Dynamic Attributes
    if (filtersData.attributes?.length > 0) {
      filtersData.attributes.forEach((attribute) => {
        dynamicData.push({
          title: attribute.attribute_name,
          slug: attribute.slug,
          number: selectedFilters?.[attribute.slug]?.length || "",
          content: attribute.values.map((val) => ({
            name: val.value_name,
            id: val.value_id,
            count: val.product_count > 0 ? val.product_count : "",
          })),
        });
      });
    }

    setFilterPopupData(dynamicData);
  }, [
    show,
    show404,
    slug,
    selectedFilters,
    products.filters,
    popularProducts.filters,
    latestProducts.filters,
    bestSellerProducts.filters,
    productsByGroup.filters,
  ]);

  const handleQuickLook = async (productId) => {
    dispatch(clearProduct());
    const res = await dispatch(fetchProductById(productId));
    if (res.payload) {
      setSelectedProduct(productId);
      setShowQuickLook(true);
    }
  };

  const handleCloseQuickLook = () => {
    setShowQuickLook(false);
    setSelectedProduct(null);
  };

  // useEffect(() => {
  //   if (!slug === "popular") {
  //     dispatch(fetchProducts({ params: { category: slug } }));
  //     // dispatch(fetchCategories());
  //     dispatch(fetchCategoryDetails(slug));
  //     // dispatch(fetchAttributes());
  //   }
  // }, [dispatch, slug]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Sorting
  const handleSort = useCallback(
    (sortOption) => {
      let newSort = "created_at";
      let newOrder = "desc";

      if (sortOption.includes("Price - Low to High")) {
        newSort = "base_price";
        newOrder = "asc";
      } else if (sortOption.includes("Price - High to Low")) {
        newSort = "base_price";
        newOrder = "desc";
      } else if (sortOption.includes("Newest")) {
        newSort = "created_at";
        newOrder = "desc";
      } else if (sortOption.includes("Alphabetically - A to Z")) {
        newSort = "name";
        newOrder = "asc";
      } else if (sortOption.includes("Alphabetically - Z to A")) {
        newSort = "name";
        newOrder = "desc";
      }

      setPage(1);
      setSort(newSort);
      setOrder(newOrder);
      setSelectedSort(sortOption);
      setSortApplied(true);
      setHasMore(true);

      const params = {
        page: 1,
        limit,
        sort: newSort,
        order: newOrder,
        search: searchQuery,
      };

      const group = allGroups?.data?.find((g) => {
        return (
          (slug === "casual-wear-products" && g.group_name === "Casual Wear") ||
          (slug === "tunic-cord-set-products" &&
            g.group_name === "Cord Set & Tunic")
        );
      });

      if (group) {
        params.group = group.group_id;
      } else if (slug === "popular-products") {
        params.popular = true;
      } else if (slug === "latest-arrival-products") {
        params.latest = true;
      } else if (slug === "best-seller-products") {
        params.bestseller = true;
      } else if (searchBar) {
        params.search = searchBar;
      } else {
        params.current_category = slug;
      }

      // ✅ Handle flat filters
      const selectedCategories = [
        ...(selectedFilters.subcategory || []),
        ...(selectedFilters.category || []),
      ];
      if (selectedCategories.length > 0) {
        params.category = selectedCategories.join(",");
      }

      if (selectedFilters.color?.length > 0) {
        params.colors = selectedFilters.color.join(",");
      }

      if (selectedFilters.size?.length > 0) {
        params.sizes = selectedFilters.size.join(",");
      }

      if (
        selectedFilters.price_min !== null &&
        selectedFilters.price_max !== null
      ) {
        params.price_min = selectedFilters.price_min;
        params.price_max = selectedFilters.price_max;
      }

      if (selectedFilters.rating !== null) {
        params.rating = selectedFilters.rating;
      }

      // ✅ Handle dynamic attributes
      Object.entries(selectedFilters).forEach(([key, values]) => {
        const knownKeys = [
          "category",
          "subcategory",
          "color",
          "size", // ← color should prevent this
          "colors",
          "sizes",
          "price_min",
          "price_max",
          "rating",
        ];
        if (
          !knownKeys.includes(key) &&
          Array.isArray(values) &&
          values.length > 0
        ) {
          params[`attribute[${key}]`] = values.join(",");
        }
      });

      showLoader();

      const fetchFn = searchBar ? fetchSearchProducts : fetchProducts;

      dispatch(fetchFn(searchBar ? { ...params } : { params }))
        .unwrap()
        .then((res) => {
          const productsData = searchBar ? res.products : res.products || [];
          setLocalProducts(productsData);
          setHasMore(productsData.length === limit);
        })
        .catch((err) => {
          console.error("Error sorting products:", err);
        })
        .finally(() => {
          hideLoader();
        });
    },
    [dispatch, hideLoader, limit, searchBar, showLoader]
  );

  const clearSorting = useCallback(() => {
    setSort("created_at");
    setOrder("desc");
    setSelectedSort("");
    setSortApplied(false);
    setPage(1);
    setHasMore(true);

    const params = {
      page: 1,
      limit,
      sort: "created_at",
      order: "desc",
      search: searchQuery,
    };

    const group = allGroups?.data?.find((g) => {
      return (
        (slug === "casual-wear-products" && g.group_name === "Casual Wear") ||
        (slug === "tunic-cord-set-products" &&
          g.group_name === "Cord Set & Tunic")
      );
    });

    if (group) {
      params.group = group.group_id;
    } else if (slug === "popular-products") {
      params.popular = true;
    } else if (slug === "latest-arrival-products") {
      params.latest = true;
    } else if (slug === "best-seller-products") {
      params.bestseller = true;
    } else if (searchBar) {
      params.search = searchBar;
    } else {
      params.current_category = slug;
    }

    const selectedCategories = [
      ...(selectedFilters.subcategory || []),
      ...(selectedFilters.category || []),
    ];
    if (selectedCategories.length > 0) {
      params.category = selectedCategories.join(",");
    }

    if (selectedFilters.color?.length > 0) {
      params.colors = selectedFilters.color.join(",");
    }

    if (selectedFilters.size?.length > 0) {
      params.sizes = selectedFilters.size.join(",");
    }

    if (
      selectedFilters.price_min !== null &&
      selectedFilters.price_max !== null
    ) {
      params.price_min = selectedFilters.price_min;
      params.price_max = selectedFilters.price_max;
    }

    if (selectedFilters.rating !== null) {
      params.rating = selectedFilters.rating;
    }

    // ✅ Dynamic attributes
    Object.entries(selectedFilters).forEach(([key, values]) => {
      const knownKeys = [
        "category",
        "subcategory",
        "color",
        "size",
        "colors",
        "sizes",
        "price_min",
        "price_max",
        "rating",
      ];
      if (
        !knownKeys.includes(key) &&
        Array.isArray(values) &&
        values.length > 0
      ) {
        params[`attribute[${key}]`] = values.join(",");
      }
    });

    showLoader();

    const fetchFn = searchBar ? fetchSearchProducts : fetchProducts;

    dispatch(fetchFn(searchBar ? { ...params } : { params }))
      .unwrap()
      .then((res) => {
        const productList = searchBar ? res.products : res.products || [];
        setLocalProducts(productList);
        setHasMore(productList.length === limit);
      })
      .catch((err) => {
        console.error("Error clearing sorting:", err);
      })
      .finally(() => {
        hideLoader();
      });
  }, [dispatch, hideLoader, limit, searchBar, showLoader]);

  // Page search box (not URL searchBar)
  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
      setPage(1);
      setHasMore(true);
      showLoader();

      const params = {
        page: 1,
        limit,
        sort,
        order,
        search: query,
      };

      const group = allGroups?.data?.find((g) => {
        return (
          (slug === "casual-wear-products" && g.group_name === "Casual Wear") ||
          (slug === "tunic-cord-set-products" &&
            g.group_name === "Cord Set & Tunic")
        );
      });

      if (group) {
        params.group = group.group_id;
      } else if (slug === "popular-products") {
        params.popular = true;
      } else if (slug === "latest-arrival-products") {
        params.latest = true;
      } else if (slug === "best-seller-products") {
        params.bestseller = true;
      } else if (searchBar) {
        params.search = searchBar;
      } else {
        params.current_category = slug;
      }

      // ✅ Merge all categories
      const selectedCategories = [
        ...(selectedFilters.subcategory || []),
        ...(selectedFilters.category || []),
      ];
      if (selectedCategories.length > 0) {
        params.category = selectedCategories.join(",");
      }

      // ✅ Colors and sizes
      if (selectedFilters.color?.length > 0) {
        params.colors = selectedFilters.color.join(",");
      }

      if (selectedFilters.size?.length > 0) {
        params.sizes = selectedFilters.size.join(",");
      }

      // ✅ Price range
      if (
        selectedFilters.price_min !== null &&
        selectedFilters.price_max !== null
      ) {
        params.price_min = selectedFilters.price_min;
        params.price_max = selectedFilters.price_max;
      }

      // ✅ Rating
      if (selectedFilters.rating !== null) {
        params.rating = selectedFilters.rating;
      }

      Object.entries(selectedFilters).forEach(([key, values]) => {
        const knownKeys = [
          "category",
          "subcategory",
          "color",
          "size", // ← color should prevent this
          "colors",
          "sizes",
          "price_min",
          "price_max",
          "rating",
        ];
        if (
          !knownKeys.includes(key) &&
          Array.isArray(values) &&
          values.length > 0
        ) {
          params[`attribute[${key}]`] = values.join(",");
        }
      });
      // 🔄 Fetch Products
      const fetchFn = searchBar ? fetchSearchProducts : fetchProducts;

      dispatch(fetchFn(searchBar ? { ...params } : { params }))
        .unwrap()
        .then((res) => {
          const productList = searchBar ? res.products : res.products || [];
          setLocalProducts(productList);
          setHasMore(productList.length === limit);
        })
        .catch((err) => {
          console.error("Error searching products:", err);
        })
        .finally(() => {
          hideLoader();
        });
    },
    [dispatch, hideLoader, limit, searchBar, showLoader]
  );

  // const getAppliedFiltersCount = () => {
  //   let count = 0;

  //   // ✅ Count all array-based filters
  //   Object.entries(selectedFilters).forEach(([key, value]) => {
  //     if (Array.isArray(value) && value.length > 0) {
  //       count += value.length;
  //     }
  //   });

  //   // ✅ Count price filter if both min & max are selected
  //   if (
  //     selectedFilters.price_min !== null &&
  //     selectedFilters.price_max !== null
  //   ) {
  //     count += 1;
  //   }

  //   // ✅ Count rating filter
  //   if (selectedFilters.rating !== null) {
  //     count += 1;
  //   }

  //   return count;
  // };
  const appliedFiltersCount = useMemo(() => {
    let count = 0;

    // ✅ Count all array-based filters
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        count += value.length;
      }
    });

    // ✅ Count price filter if both min & max are selected
    if (
      selectedFilters.price_min !== null &&
      selectedFilters.price_max !== null
    ) {
      count += 1;
    }

    // ✅ Count rating filter
    if (selectedFilters.rating !== null) {
      count += 1;
    }

    return count;
  }, [selectedFilters]);

  // const appliedFiltersCount = getAppliedFiltersCount();
  if (!validated) return null;

  if (show404) {
    return (
      // <section className="py-5 text-center not-found-section">
      //   <div className="container">
      //     <h3 className="display-5">404</h3>
      //     <p className="leading">We could not find the page you were looking for. Please click the button below to go back to our website.</p>
      //     <button
      //         onClick={() => router.push("/")}
      //         className="fill-btn text-white d-inline-block mx-auto mt-4 "
      //     >
      //         Continue Shopping
      //   </button>
      //   </div>
      // </section>

      <section className="section-py">
        <div className="container-xs px-3">
          <div className="text-center">
            <div>
              <Image
                src="/assets/images/not-found/Not-Found.webp"
                width={928}
                height={672}
                loading="lazy"
                className="img-fluid"
                alt="Blank-Wishlist-Image "
              />
            </div>
            <div className="mt-4 pt-2">
              {/* <ComanButton title="Back to Home" href="/" /> */}
              <CommonBtn
                variant="hover-outline"
                as={Link}
                href="/"
                className="position-relative px-3"
              >
                <span
                  className={`fs-5 fw-normal text-capitalize z-1 text-nowrap`}
                >
                  Back to Home
                </span>
              </CommonBtn>
            </div>
          </div>
        </div>
      </section>
    );
  } else {
    return (
      <>
        <Image
          src="/assets/images/saree/bg-poster/bg-poster.webp"
          alt="Main Poster"
          fetchPriority="high"
          width={1}
          height={1}
          style={{ position: "absolute", width: 1, height: 1, opacity: 0 }}
        />
        <MainPoster
          bgimg="/assets/images/saree/bg-poster/bg-poster.webp"
          title={
            categoryDetails?.data?.category_name ||
            (slug === "popular-products" && "Popular Products") ||
            (slug === "latest-arrival-products" && "Latest Arrival") ||
            (slug === "tunic-cord-set-products" && "Tunic & Cord Set") ||
            (slug === "casual-wear-products" && "Casual Wear") ||
            (slug === "best-seller-products" && "Best Seller") ||
            (searchBar &&
              `${searchBar.replace(/\b\w/g, (char) => char.toUpperCase())}`)
          }
          content="Discover the latest in fashion with Kalindi's new collection, where elegance meets innovation. Our fresh designs feature exquisite patterns, luxurious fabrics, and impeccable craftsmanship, ensuring you stand out on every occasion."
        />

        <section className="py-5 page-bg common-bg-img">
          <div className="container-xs-one px-3">
            <div className="d-flex flex-column  flex-md-row align-items-center justify-content-between gap-3">
              <div className="flex-shrink-1 order-md-1">
                <SearchBox
                  className="filter-search"
                  // type="pagefilter"
                  placeholder="Search Now"
                  onSearch={handleSearch}
                />
              </div>
              <div className="category-filter flex-grow-1 order-md-0">
                <div className="d-flex align-items-center gap-2 gap-sm-3">
                  {/* <Button
                    variant="link"
                    className="rounded-pill text-decoration-none fw-normal btn-common "
                    onClick={handleOpen}
                    // btn-all-active  <---  When this All button is active, this className is added to the button
                  >
                    All
                  </Button>
  
                  <div className="vr-line d-none d-sm-block"></div> */}
                  <div className="d-flex align-items-center flex-wrap filter-gap">
                    <div className="position-relative">
                      <Button
                        variant="link"
                        className={`rounded-pill text-decoration-none btn-filter ${
                          filtersAppliedRef.current ? "btn-filter-active" : ""
                        }`}
                        onClick={handleShow}
                      >
                        <div className="d-flex align-items-center">
                          <div className="d-flex align-items-center gap-2">
                            <FilterIcon />
                            <p className="text-nowrap ">
                              {appliedFiltersCount > 0 &&
                                `${appliedFiltersCount}`}{" "}
                              Filters
                            </p>
                          </div>

                          {filtersAppliedRef.current && (
                            <div
                              className="position-absolute top-50 translate-middle-y clear-btn d-flex align-items-center justify-content-center rounded-circle "
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpen();
                              }}
                            >
                              <CloseIcon size={10} />
                            </div>
                          )}
                        </div>
                      </Button>
                      {filtersAppliedRef.current && (
                        <span className="filter-dot rounded-circle position-absolute"></span>
                      )}
                    </div>
                    {/* <Button
                        variant="link"
                        className="rounded-pill text-decoration-none fw-medium btn-common "
                      >
                        <p>Sort By</p>
                      </Button> */}

                    <Dropdown className="filter-dropdown" align="bottom">
                      <Dropdown.Toggle
                        variant="link"
                        className={`rounded-pill text-decoration-none fw-normal btn-common ${
                          selectedSort ? "btn-filter-active" : ""
                        }`}
                        id="dropdown-basic"
                      >
                        <p>
                          Sort By {selectedSort && `: `}
                          <b style={{ fontWeight: "500" }}>{selectedSort}</b>
                        </p>
                        {selectedSort && (
                          <div
                            className="position-absolute top-50 translate-middle-y clear-btn rounded-circle d-flex align-items-center justify-content-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearSorting(); // Call the clearSorting function directly
                            }}
                          >
                            <CloseIcon size={10} />
                          </div>
                        )}
                      </Dropdown.Toggle>
                      {/* <Dropdown.Menu className="rounded-3 border-0 drop-menu">
                            {sortbymenucontent.map((item, i) => {
                              return (
                                <Dropdown.Item
                                  key={i}
                                  className="fw-normal"
                                  onClick={() => {
                                    const sortOption = item.name.replace(/\s+/g, "_").toLowerCase();
                                    dispatch(fetchProducts({ params: { category: slug, sort: sortOption } }));
                                  }}
                                >
                                  {item.name}
                                </Dropdown.Item>
                              );
                            })}
                          </Dropdown.Menu> */}

                      <Dropdown.Menu className="rounded-3 border-0 drop-menu">
                        {[
                          "Newest",
                          "Featured",
                          "Price - Low to High",
                          "Price - High to Low",
                          "Alphabetically - A to Z",
                          "Alphabetically - Z to A",
                        ].map((item, i) => (
                          <Dropdown.Item
                            key={i}
                            className={`fw-normal d-flex align-items-center justify-content-between ${
                              selectedSort === item ? "active" : ""
                            }`}
                            style={
                              selectedSort === item ? { color: "#2D5852" } : {}
                            }
                            onClick={() => handleSort(item)}
                          >
                            {item}
                            {selectedSort === item && (
                              <span
                                className="ms-2"
                                style={{ color: "#2D5852" }}
                              >
                                ✔
                              </span>
                            )}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
                {/* Permission (Are Sure Clear Filter) Popup  */}
                <PermissionPopup
                  Open={open}
                  Hide={handleHide}
                  Close={handleHide}
                  onConfirm={async () => {
                    const params = {
                      page: 1,
                      limit,
                      sort: "created_at",
                      order: "desc",
                    };

                    // Handle different slugs properly
                    if (slug === "popular-products") {
                      params.popular = true;
                    } else if (slug === "latest-arrival-products") {
                      params.latest = true;
                    } else if (slug === "best-seller-products") {
                      params.bestseller = true;
                    } else if (searchBar) {
                      params.search = searchBar;
                    } else {
                      const group = allGroups?.data?.find((g) => {
                        return (
                          (slug === "casual-wear-products" &&
                            g.group_name === "Casual Wear") ||
                          (slug === "tunic-cord-set-products" &&
                            g.group_name === "Cord Set & Tunic")
                        );
                      });

                      if (group) {
                        params.group = group.group_id;
                      } else {
                        params.current_category = slug;
                      }
                    }

                    showLoader();
                    setPage(1);
                    setHasMore(true);
                    setLocalProducts([]); // Reset product list

                    try {
                      let response;
                      if (searchBar) {
                        response = await dispatch(
                          fetchSearchProducts({ ...params })
                        ).unwrap();
                      } else {
                        response = await dispatch(
                          fetchProducts({ params })
                        ).unwrap();
                      }

                      setLocalProducts(response?.products || []);
                      setTotalRecords(response?.pagination?.total || 0);
                      setHasMore((response?.products?.length || 0) === limit);
                    } catch (err) {
                      console.error("Error clearing filters:", err);
                      setHasMore(false);
                    } finally {
                      hideLoader();
                    }

                    // Reset filters
                    setSelectedFilters({
                      subcategory: [],
                      attributes: {},
                      price_min: null,
                      price_max: null,
                      rating: null,
                    });

                    setFiltersApplied(false);
                    filtersAppliedRef.current = false;

                    handleHide(); // Close popup
                  }}
                  content="Are you sure, you want to clear all applied filters?"
                />

                {/* Filter Popup */}
                <FilterPopup
                  Open={show}
                  Close={handleClose}
                  data={filterPopupData}
                  fullscreen="sm-down"
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                  onApply={handleApplyFilters}
                  setFiltersApplied={setFiltersApplied}
                  filtersApplied={filtersApplied}
                  filters={products.filters || bestSellerProducts.filters}
                />
              </div>
            </div>
            <div className="mt-4 pt-3">
              <div className="gap-4 custom-grid-four">
                <ProductCardMain
                  className="overflow-hidden shadow-none border-0"
                  classNameMain="d-flex flex-column justify-content-center align-items-center"
                  productsGrid={localProducts}
                  type="gridview"
                  onClick={handleQuickLook}
                  searchBar={searchBar}
                />
              </div>
              {scrollLoading && scrollLoading && page > 1 && <ScrollLoader />}
            </div>

            {/* <div className="d-flex align-items-center justify-content-center mt-4 pt-1">
              {totalRecords > limit && (
                <div className="d-flex align-items-center justify-content-center mt-4 pt-1">
                  <Pagination
                    currentPage={page}
                    limit={limit}
                    totalRecords={totalRecords}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div> */}
          </div>
        </section>
        {showQuickLook && (
          <QuickLookPopup
            show={showQuickLook}
            onHide={handleCloseQuickLook}
            fullscreen="md-down"
            product={product}
          />
        )}
      </>
    );
  }
};
export default CategoryPages;
